import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useApi } from "../../hooks/useApi";
import { Logo } from "../Logo";
import { MyPhoto } from "./PhotoPicker";

const NavItem = ({ to, label, icon: Icon, badge, end }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
    className={({ isActive }) =>
      `flex shrink-0 items-center gap-3 whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-semibold transition-colors lg:rounded-xl ${isActive ? "bg-[#39FF14] text-[#0A0E1A]" : "text-slate-300 hover:bg-[#162032] hover:text-white"}`
    }
  >
    <Icon className="h-4 w-4" />
    {label}
    {badge > 0 && <span className="ml-auto rounded-full bg-[#FF3B30] px-2 py-0.5 font-mono-gd text-[10px] text-white">{badge}</span>}
  </NavLink>
);

export function AppShell({ items, unreadPath, home, children, extra }) {
  const { me, memberships, membership, setTeamId, logout } = useAuth();
  const nav = useNavigate();
  const unread = useApi(unreadPath);
  const count = unread.data?.count ?? 0;
  const out = () => { logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-[#0A0E1A] text-slate-100 lg:flex">
      <aside className="no-print sticky top-0 z-30 border-b border-[#1E293B] bg-[#0A0E1A]/90 backdrop-blur-xl lg:h-screen lg:w-64 lg:shrink-0 lg:border-b-0 lg:border-r">
        <div className="flex items-center justify-between px-4 py-3 lg:flex-col lg:items-start lg:gap-8 lg:px-5 lg:py-7">
          <Logo to={home} />
          <div className="flex items-center gap-2 lg:hidden">
            <MyPhoto size="h-8 w-8" testid="my-photo-button-mobile" />
            <button data-testid="logout-button-mobile" onClick={out} className="rounded-full p-2 text-slate-400 hover:text-white"><LogOut className="h-5 w-5" /></button>
          </div>
        </div>
        {memberships.length > 1 && (
          <div className="px-4 pb-3 lg:px-5">
            <select
              data-testid="team-switcher"
              value={membership?.team_id}
              onChange={(e) => setTeamId(e.target.value)}
              className="w-full rounded-xl border border-[#1E293B] bg-[#162032] px-3 py-2 text-sm text-white"
            >
              {memberships.map((m) => <option key={m.team_id} value={m.team_id}>{m.team_name} · {m.web_role ?? m.role}</option>)}
            </select>
          </div>
        )}
        {extra}
        <nav className="flex gap-1 overflow-x-auto px-4 pb-3 lg:flex-col lg:px-3">
          {items.map((it) => <NavItem key={it.to} {...it} badge={it.unread ? count : it.badge ?? 0} />)}
        </nav>
        <div className="absolute bottom-0 hidden w-full border-t border-[#1E293B] p-5 lg:block">
          <div className="flex items-center gap-3">
            <MyPhoto />
            <div className="min-w-0">
              <p data-testid="current-user-name" className="truncate text-sm font-semibold text-white">{me?.name}</p>
              <p className="truncate text-xs text-slate-400">{membership ? `${membership.team_name} · ${membership.web_role ?? membership.role}` : me?.org ?? "Recruiter"}</p>
            </div>
          </div>
          <button data-testid="logout-button" onClick={out} className="mt-3 flex items-center gap-2 text-sm text-slate-400 hover:text-[#39FF14]"><LogOut className="h-4 w-4" />Log out</button>
        </div>
      </aside>
      <main className="print-area min-w-0 flex-1 px-4 py-8 sm:px-8 lg:px-12 lg:py-12">
        {children ?? <Outlet />}
      </main>
    </div>
  );
}
